/**
 * Graft a host scene's deep children into the `.tscn` sub-scene it instanced, where
 * `sceneTreeBuilder` stopped at the instance node with an `instanceSubPath`. The GLB
 * flavour needs its own tolerant matcher. It must stay pure: the sub-scene is a shared
 * cache entry, so this copies along the changed spine and shares everything else.
 */
import { hostChildOf, scopeOf, withFallbackScope } from './liveNode';
import { layerRawOverride } from './layerRawOverride';
import { rehomeOverride } from './rehomeOverride';
import { warn } from '../logger';
import { endAboveRoot, InstanceNameTable, OUTSIDE, } from './instanceNameTable';
import { UNIQUE_NODE_PREFIX } from '../godot/string/nodePath';
import { contentTrailSegment } from '../parser/idTrail';
import { joinPath } from '../utils/nodePath';
import { enteredThroughBase, followedChild } from './parentContentTrail';
import { addAtIndex, insertAtIndex } from '../godot/scene/siblingIndex';
/**
 * The sub-scene root's children with the host's overrides folded in. A grafted node
 * carries the outer scope, and a node an override reached carries its own scope plus
 * the resources an override names (`rehomeOverride`).
 *
 * @param rootChildren the loaded sub-scene root's children (never mutated)
 * @param hostChildren the instancing node's children, deep and direct alike
 * @param names the sub-scene's own `%Name` table and the settings the instance carries
 */
export function graftInstanceChildren(rootChildren, hostChildren, scopes, names) {
    const graft = new Graft(rootChildren, scopes, names);
    for (const [index, hosted] of hostChildren.entries()) {
        // A setting lands at its heading, so only the host children written after it read it.
        graft.landUpTo(index);
        graft.seat(hosted);
    }
    return graft.finish(hostChildren.length);
}
/** One graft in progress: the instance's children, which each host child changes in turn. */
class Graft {
    scopes;
    names;
    /** Written only by `landUpTo` and the seating of a host child. */
    grafted;
    table;
    placements = new Map();
    escaped = [];
    // A child that stays in the instance: an override that matches no node, as Godot's drop is not
    // modelled, and a child over a base the cache has not composed, whose next collapse seats it. Kept
    // out of `grafted` until the end, so no later sub-path, fold or setting lands on it.
    misplaced = [];
    constructor(rootChildren, scopes, names) {
        this.scopes = scopes;
        this.names = names;
        this.grafted = [...rootChildren];
        this.table = new InstanceNameTable(names, this.placements);
    }
    landUpTo(settingCount) {
        this.grafted = this.table.landUpTo(this.grafted, settingCount);
    }
    finish(settingCount) {
        return {
            children: [...this.table.landUpTo(this.grafted, settingCount), ...this.misplaced],
            uniqueNames: this.table,
            uniqueParentPlacements: this.placements,
            escaped: this.escaped,
            handOffDecisions: this.table.handOffDecisions,
        };
    }
    /** Seats `hosted`, and records where under `key`, the path a setting for it names. */
    seat(hosted, key = placementKey(hosted)) {
        const { child, status } = followedChild(hosted, this.names.followTrail);
        // A child re-anchored at a nested instance keeps the scene it was authored in.
        const stamped = withFallbackScope(child, this.scopes.outer);
        if (status === 'waiting')
            this.misplaced.push(stamped);
        else if (status === 'missing')
            this.escaped.push({ child, scope: stamped.scope, end: null });
        else if (child.instanceSubPath)
            this.seatAtSubPath(child, stamped, key);
        else
            this.seatDirect(child, stamped, key);
    }
    seatDirect(child, stamped, key) {
        const folded = child.overridesExistingNode
            ? foldOrWarn(this.grafted, stamped, this.scopes.content)
            : null;
        if (folded)
            this.grafted = folded;
        else if (child.overridesExistingNode)
            this.misplaced.push(stamped);
        else {
            insertAtIndex(this.grafted, stamped);
            if (key)
                this.placements.set(key, child.name);
        }
    }
    seatAtSubPath(child, stamped, key) {
        const subPath = child.instanceSubPath;
        const segments = this.table.walk(subPath, this.grafted);
        const inside = segments?.[0] === OUTSIDE ? null : segments;
        const next = inside && graftAt(this.grafted, inside, stamped, this.scopes.content);
        if (!inside || !next) {
            this.seatUnwalked(child, stamped, segments, key);
            return;
        }
        this.grafted = next;
        if (key)
            this.placements.set(key, joinPath(inside.join('/'), child.name));
    }
    /** A child whose `parent=` walk found nothing, so its `parent_id_path=` is tried (packed_scene.cpp:161-163). */
    seatUnwalked(child, stamped, segments, key) {
        const { hasUnloadedBase } = this.names;
        const entered = hasUnloadedBase ? undefined : enteredThroughBase(child, this.names);
        if (entered?.status === 'waiting')
            this.misplaced.push(stamped);
        else if (entered)
            this.seat(entered.child, key ?? joinPath(child.instanceSubPath, child.name));
        else if (child.overridesExistingNode || hasUnloadedBase) {
            // A base still loading seats the child at its next collapse, so only an override warns.
            if (child.overridesExistingNode) {
                warn(`[graftInstanceChildren] "${child.name}" targets "${child.instanceSubPath}", which the instanced scene does not contain — appending at its root`);
            }
            this.misplaced.push(stamped);
        }
        else {
            this.escaped.push({
                child,
                scope: stamped.scope,
                end: walkEnd(segments, child.instanceSubPath, this.table),
            });
        }
    }
}
/**
 * The key a placement records `hostChild` under: its sub-path and its name, as authored, with the
 * segment of its content trail for a sub-path the trail finds. Undefined where the sub-path passes
 * through no `%Name` and no trail, as the path as written then needs no record.
 *
 * @canonical The key of a host child's placement, as `uniqueParentPlacements` keys it
 */
export function placementKey(hostChild) {
    const { instanceSubPath, parentContentTrail, name } = hostChild;
    if (parentContentTrail !== undefined)
        return joinPath(contentTrailSegment(parentContentTrail), name);
    return instanceSubPath?.includes(UNIQUE_NODE_PREFIX) ? joinPath(instanceSubPath, name) : undefined;
}
/**
 * Where a host child that the instance could not seat goes on: the path that leaves the instance,
 * or null where its walk found nothing the scene above answers.
 */
function walkEnd(segments, subPath, table) {
    if (segments === null)
        return endAboveRoot(subPath, table.claimed);
    return segments[0] === OUTSIDE ? segments.join('/') : null;
}
/**
 * Copy `siblings` with `child` grafted at `segments`, or `null` when the path
 * names nothing. Only the nodes along the path are new objects. `siblingScope` is
 * the scope `siblings` resolve against unless a node carries its own.
 */
export function graftAt(siblings, segments, child, siblingScope) {
    const [head, ...rest] = segments;
    const index = siblings.findIndex((n) => n.name === head);
    if (index === -1)
        return null;
    const target = siblings[index];
    const targetScope = scopeOf(target, siblingScope);
    let replacement;
    if (target.instance) {
        // The path reaches another instance, whose sub-scene is not merged yet. Re-anchor
        // here with the remaining path, or as a direct child where none remains: when that
        // node collapses, it runs this same graft and resolves the rest.
        replacement = { ...target, children: [...target.children, reanchored(child, rest)] };
    }
    else if (rest.length > 0) {
        const deeper = graftAt(target.children, rest, child, targetScope);
        replacement = deeper ? { ...target, children: deeper } : null;
    }
    else {
        replacement = { ...target, children: attach(target.children, child, targetScope) };
    }
    if (!replacement)
        return null;
    const copy = [...siblings];
    copy[index] = replacement;
    return copy;
}
/** `child` with `rest` as its sub-path below the instance it re-anchors at, or none when `rest` is empty. */
function reanchored(child, rest) {
    const { instanceSubPath: _authored, ...direct } = child;
    if (rest.length === 0)
        return direct;
    return { ...direct, instanceSubPath: rest.join('/'), reanchoredFrom: hostChildOf(child) };
}
/**
 * `siblings` with `child` attached as a new node, or with an override folded onto
 * the node already there. Appending an override would leave two nodes of one name
 * where Godot has one, with the properties on a duplicate nothing references.
 */
function attach(siblings, child, siblingScope) {
    const folded = child.overridesExistingNode ? foldOrWarn(siblings, child, siblingScope) : null;
    return folded ?? addAtIndex(siblings, child);
}
/**
 * {@link foldOverride}, warning when no sibling has the override's name. The caller
 * then adds the override as a node: visible beats silently dropped.
 */
function foldOrWarn(siblings, override, siblingScope) {
    const folded = foldOverride(siblings, override, siblingScope);
    if (!folded) {
        warn(`[graftInstanceChildren] override "${override.name}" matches no node inside the instanced scene — adding it instead`);
    }
    return folded;
}
/**
 * `siblings` with the override's raw properties layered onto the node of its name,
 * and the override's children attached under it, or `null` when none has the name.
 * Each reference resolves in the scene that wrote it: the override's and its
 * children's in the override's scope, the node's own and its children's in theirs.
 */
function foldOverride(siblings, override, siblingScope) {
    const index = siblings.findIndex((n) => n.name === override.name);
    if (index === -1)
        return null;
    const existing = siblings[index];
    const existingScope = scopeOf(existing, siblingScope);
    const overrideScope = scopeOf(override, existingScope);
    const { raw, scope } = rehomeOverride(override.rawProperties, overrideScope, existingScope);
    const layered = Object.keys(raw).length === 0 ? existing : { ...existing, ...layerRawOverride(existing, existing, raw) };
    const children = attachAll(layered.children, override.children, overrideScope, scope);
    const copy = [...siblings];
    copy[index] = scope === siblingScope ? { ...layered, children } : { ...layered, children, scope };
    return copy;
}
/**
 * `siblings` with each of `children` attached. The host's parser seats a node whose
 * `parent=` names an override under that override, so it arrives as the override's child,
 * written in `childScope`.
 */
function attachAll(siblings, children, childScope, siblingScope) {
    let attached = [...siblings];
    for (const child of children) {
        attached = attach(attached, withFallbackScope(child, childScope), siblingScope);
    }
    return attached;
}
//# sourceMappingURL=graftInstanceChildren.js.map